import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LifeBuoy, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
    "w-full rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#0D9488] focus:bg-white";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]";

const ProblemForm = ({ t }) => {
    const s = t.support.problem;
    const [form, setForm] = useState({ name: "", email: "", subject: s.subjects[0], message: "" });
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                subject: form.subject,
                message: form.message.trim(),
                type: "support",
            });
            toast.success(s.success);
            setForm({ name: "", email: "", subject: s.subjects[0], message: "" });
        } catch {
            toast.error(t.contact.error);
        } finally {
            setSending(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="flex h-full flex-col rounded-[2rem] border border-[#E2E8F0] bg-white p-8 shadow-sm md:p-10"
            data-testid="support-problem-form"
        >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#0D9488]/30 bg-[#0D9488]/10">
                <LifeBuoy size={22} className="text-[#0D9488]" />
            </div>
            <h3 className="mt-6 text-xl font-bold text-[#0F172A]">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.desc}</p>
            <div className="mt-6">
                <label htmlFor="support-name" className={labelCls}>{t.contact.name}</label>
                <input id="support-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="support-name-input" />
            </div>
            <div className="mt-5">
                <label htmlFor="support-email" className={labelCls}>{t.contact.email}</label>
                <input id="support-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="support-email-input" />
            </div>
            <div className="mt-5">
                <label htmlFor="support-subject" className={labelCls}>{s.subject}</label>
                <select id="support-subject" value={form.subject} onChange={set("subject")} className={`${inputCls} bg-[#F8FAFC]`} data-testid="support-subject-select">
                    {s.subjects.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            </div>
            <div className="mt-5 flex-1">
                <label htmlFor="support-message" className={labelCls}>{s.message}</label>
                <textarea id="support-message" required minLength={2} rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="support-message-input" />
            </div>
            <button
                type="submit"
                disabled={sending}
                className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-[#0D9488] px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#0E4B57] disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="support-submit-button"
            >
                <Send size={16} />
                {sending ? t.contact.sending : s.submit}
            </button>
        </form>
    );
};

const DeletionForm = ({ t }) => {
    const d = t.support.deletion;
    const [form, setForm] = useState({ name: "", email: "", reason: "", confirm: false });
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/delete-account`, {
                name: form.name.trim(),
                email: form.email.trim(),
                reason: form.reason.trim() || null,
                confirm: form.confirm,
            });
            toast.success(d.success);
            setForm({ name: "", email: "", reason: "", confirm: false });
        } catch {
            toast.error(t.contact.error);
        } finally {
            setSending(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="flex h-full flex-col rounded-[2rem] border border-[#D97706]/30 bg-white p-8 shadow-sm md:p-10"
            data-testid="deletion-form"
        >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#D97706]/30 bg-[#FEF3C7]">
                <Trash2 size={22} className="text-[#D97706]" />
            </div>
            <h3 className="mt-6 text-xl font-bold text-[#0F172A]">{d.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#475569]">{d.intro}</p>
            <div className="mt-6">
                <label htmlFor="deletion-name" className={labelCls}>{d.name}</label>
                <input id="deletion-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="deletion-name-input" />
            </div>
            <div className="mt-5">
                <label htmlFor="deletion-email" className={labelCls}>{d.email}</label>
                <input id="deletion-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="deletion-email-input" />
            </div>
            <div className="mt-5">
                <label htmlFor="deletion-reason" className={labelCls}>{d.reason}</label>
                <textarea id="deletion-reason" rows={3} value={form.reason} onChange={set("reason")} className={`${inputCls} resize-none`} data-testid="deletion-reason-input" />
            </div>
            <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#475569]" data-testid="deletion-confirm-label">
                <input
                    type="checkbox"
                    required
                    checked={form.confirm}
                    onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.checked }))}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#D97706]"
                    data-testid="deletion-confirm-checkbox"
                />
                {d.confirm}
            </label>
            <button
                type="submit"
                disabled={sending || !form.confirm}
                className="mt-7 flex w-full items-center justify-center gap-3 rounded-full border border-[#D97706]/50 bg-[#FEF3C7] px-7 py-4 text-sm font-bold text-[#B45309] transition-colors duration-300 hover:bg-[#FDE68A] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="deletion-submit-button"
            >
                <Trash2 size={16} />
                {sending ? t.contact.sending : d.submit}
            </button>
        </form>
    );
};

export const SupportSection = ({ t }) => (
    <section id="support" className="scroll-mt-28 bg-white py-24 md:py-32" data-testid="support-section">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mb-14 max-w-2xl"
            >
                <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#0D9488] md:text-sm">{t.support.overline}</p>
                <h2 className="font-display mt-4 text-4xl leading-tight tracking-tight text-[#0F172A] md:text-5xl">{t.support.title}</h2>
                <p className="mt-5 text-base leading-relaxed text-[#475569] md:text-lg">{t.support.sub}</p>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 gap-8 lg:grid-cols-2"
            >
                <ProblemForm t={t} />
                <DeletionForm t={t} />
            </motion.div>
        </div>
    </section>
);
